import Logs from "@/components/common/log/logs";
import Filter from "@/components/ui/filters";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageSortFilters } from "@/hooks/use-page-fs";
import SortDropdown from "@/pages/procesos/components/SortDropdown";
import { useDownloadLogs, useGetLogs } from "@/queries/process";
import { useGetUsersAsInfinite } from "@/queries/users/users.api";
import {
  filterTypesEnum,
  searchTypesEnum,
  sortDirectionEnum,
  type FilterTypeList,
  type SortOptionsTypes,
} from "@/types/common/filters";
import type { LogItemType } from "@/types/common/logs";
import { mergeFilters } from "@/utils/filters";
import { cn } from "@/utils/theme";
import { Button, Icon } from "@elaniintc/goes-design-system";
import { useEffect, useState } from "react";

const sortFields: SortOptionsTypes[] = [
  { label: "Fecha", value: "created_at" },
  { label: "Usuario", value: "causer.name" },
];

const FilterFields: FilterTypeList[] = [
  {
    label: "Fecha de creación",
    type: filterTypesEnum.dates,
    field: "created_at",
  },
  {
    label: "Creador de la entidad",
    type: filterTypesEnum.textsearch,
    field: "causer_id",
    searchField: "search",
    query: useGetUsersAsInfinite,
    searchType: searchTypesEnum.user,
  },
];

interface DetailsHistoryProps {
  entityId: string;
  entityType: "entity" | "registry";
}
const DetailsHistory = ({ entityId, entityType }: DetailsHistoryProps) => {
  const {
    filters,
    orderField,
    orderDirection,
    handleApplySort,
    selectedEntities,
    setSelectedEntities,
    handleFilterChange,
  } = usePageSortFilters({
    defaultOrderField: "created_at",
  });
  const [recentLogs, setRecentLogs] = useState<LogItemType[]>([]);
  const [yesterdayLogs, setYesterdayLogs] = useState<LogItemType[]>([]);
  const [pastLogs, setPastLogs] = useState<LogItemType[]>([]);

  // Build filter object for API
  const queryFilters = {
    ...(filters.length > 0 ? mergeFilters(filters) : {}),
    entity:
      entityType === "entity"
        ? "expedient_base_entity"
        : "expedient_base_registry",
    entity_id: entityId,
  };

  const params = {
    order_by: orderField
      ? `${orderDirection === "desc" ? "!" : ""}${orderField}`
      : "!created_at",
    relationships: "causer",
  };

  const {
    data: logsResponse,
    isLoading,
    error,
  } = useGetLogs({
    filters: queryFilters,
    params,
  });
  const { mutateAsync: downloadLogs, isPending } = useDownloadLogs({
    filters: queryFilters,
    params,
  });
  useEffect(() => {
    if (!logsResponse?.data) return;

    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);

    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(todayStart.getDate() - 1);

    const formatted: LogItemType[] = logsResponse.data.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (log: any, idx: number) => ({
        id: Number(log.id ?? idx),
        description: log?.properties?.attributes?.name ?? "-",
        action: log?.description ?? "-",
        author: log?.causer?.email ?? "Desconocido",
        date: log?.created_at ? new Date(log.created_at) : new Date(),
        subject_type: log?.subject_type,
        subject: log?.subject ?? null,
        properties: log?.properties ?? {},
      }),
    );

    const recent: LogItemType[] = [];
    const yesterday: LogItemType[] = [];
    const past: LogItemType[] = [];

    formatted.forEach((item) => {
      const date = item.date;
      if (date >= todayStart) {
        recent.push(item);
      } else if (date >= yesterdayStart && date < todayStart) {
        yesterday.push(item);
      } else {
        past.push(item);
      }
    });

    setRecentLogs(recent);
    setYesterdayLogs(yesterday);
    setPastLogs(past);
  }, [logsResponse]);

  const renderBlocks = () => {
    const blocks = [
      { title: "Recientemente", items: recentLogs },
      { title: "Ayer", items: yesterdayLogs },
      { title: "Pasados", items: pastLogs },
    ];

    const sortedBlocks =
      orderDirection === sortDirectionEnum.asc ? blocks.reverse() : blocks;

    return sortedBlocks.map(
      (block) =>
        block.items.length > 0 && (
          <Logs key={block.title} title={block.title} items={block.items} />
        ),
    );
  };

  return (
    <div className="flex flex-col gap-3 pt-3">
      <div className="flex justify-between">
        <div className="flex space-x-3">
          <SortDropdown
            buttonSize="sm"
            currentOrderField={orderField}
            currentOrderDirection={orderDirection}
            sortOptions={sortFields}
            onClick={handleApplySort}
          />
          <Filter
            buttonSize="sm"
            filters={FilterFields}
            currentFilters={filters}
            onCurrentFiltersChange={handleFilterChange}
            selecteds={selectedEntities}
            onSetSelecteds={setSelectedEntities}
          />
        </div>
        <Button
          variant="quiet"
          onClick={() => downloadLogs()}
          className={cn(
            "leading-2xl shadow-custom py-1.5 px-2 rounded-lg min-w-9 gap-2 font-semibold text-body text-sm font-geist cursor-pointer relative",
            isPending
              ? "border-neutral-silent/20 text-on-disabled"
              : "border-neutral shadow-card text-body",
          )}
          disabled={isPending || logsResponse?.data.length === 0}
        >
          {isPending && (
            <Icon type="far" name="spinner-third" className="animate-spin" />
          )}
          Exportar CSV
          <Icon type="far" name="file-spreadsheet" />
        </Button>
      </div>

      <div className="bg-neutral-object p-4 rounded-lg border border-neutral-quiet [@media(max-height:800px)]:max-h-full h-[calc(100vh-275px)] overflow-y-auto">
        {isLoading ? (
          <div role="status" className="max-w-sm animate-pulse">
            <Skeleton className="h-3 rounded-full w-48 mb-4" />
            <Skeleton className="h-2 rounded-full max-w-95 mb-2.5" />
            <Skeleton className="h-2 rounded-full max-w-95 mb-2.5" />
            <Skeleton className="h-2 rounded-full max-w-95 mb-2.5" />
          </div>
        ) : error ? (
          <p className="text-danger">Error al cargar registros</p>
        ) : (
          <div className="space-y-10" onClick={() => {}}>
            {renderBlocks()}
          </div>
        )}
      </div>
    </div>
  );
};

export { DetailsHistory };
